import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CategorySection from './components/CategorySection';
import AddProduct from './pages/AddProduct';
import { fetchProducts } from './services/api';
import { Product, Category } from './types/Product';
import './App.css';

console.log('App component module initialized');

const AppContent: React.FC = () => {
  console.log('App component rendering');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [region, setRegion] = useState<'US' | 'CA' | 'OTHER'>('US');
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('App component mounted, starting to load products...');
    const loadProducts = async () => {
      try {
        console.log('loadProducts function called');
        const shortcutId = new URLSearchParams(window.location.search).get('id');
        console.log('Fetching products with shortcutId:', shortcutId);
        
        const response = await fetchProducts(shortcutId || undefined);
        console.log('Products fetched successfully:', response);
        console.log('Products array:', response.products);
        console.log('Categories array:', response.categories);
        
        setProducts(response.products);
        setCategories(response.categories);
        setRegion(response.userRegion);
        
        // Handle category from URL
        const categoryId = location.pathname.substring(1); // Remove leading slash
        if (categoryId && response.categories.some(cat => cat.id === categoryId)) {
          setOpenCategories(new Set([categoryId]));
        }
        // Open the category containing the shortcut product, or the first category
        else if (shortcutId) {
          const product = response.products.find((p: Product) => p.shortcutId === shortcutId);
          if (product) {
            setOpenCategories(new Set([product.category]));
          }
        } else if (response.categories.length > 0) {
          setOpenCategories(new Set([response.categories[0].id]));
        }
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [location.pathname]);

  // Add debug logs for the current state
  console.log('Current state:', {
    productsCount: products.length,
    categoriesCount: categories.length,
    region,
    openCategories: Array.from(openCategories),
    loading,
    error
  });

  const toggleCategory = (categoryId: string) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
        navigate('/');
      } else {
        newSet.add(categoryId);
        navigate(`/${categoryId}`);
      }
      return newSet;
    });
  };

  const getProductsByCategory = (categoryId: string) => {
    return products.filter(product => product.category === categoryId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <div className="text-dark-text text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>DealPortal - Best Amazon Deals & Reviews</title>
        <meta name="description" content="Discover the best Amazon deals, product reviews, and recommendations. Shop smarter with DealPortal's curated selection and TikTok video reviews." />
        <meta name="keywords" content="amazon deals, product reviews, tiktok reviews, best prices, online shopping" />
        <meta property="og:title" content="DealPortal - Best Amazon Deals & Reviews" />
        <meta property="og:description" content="Discover the best Amazon deals, product reviews, and recommendations. Shop smarter with DealPortal's curated selection." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="min-h-screen bg-dark-primary">
        <header className="bg-dark-secondary p-4 shadow-lg">
          <div className="container mx-auto">
            <h1 className="bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-3xl font-bold text-transparent">
              DealPortal
            </h1>
            <p className="mt-2 text-dark-text-secondary">
              Discover Amazing Deals on Amazon {region === 'CA' ? 'Canada' : 'US'}
            </p>
          </div>
        </header>

        <main className="container mx-auto p-4">
          <Routes>
            <Route path="/" element={
              <div className="container mx-auto px-4 py-8">
                <h1 className="text-4xl font-bold text-dark-text mb-8">Deal Portal</h1>
                {categories.map(category => (
                  <CategorySection
                    key={category.id}
                    category={category}
                    products={products.filter(p => p.category === category.id)}
                    isOpen={openCategories.has(category.id)}
                    region={region}
                    onToggle={() => toggleCategory(category.id)}
                  />
                ))}
              </div>
            } />
            <Route path="/:categoryId" element={
              <div className="container mx-auto px-4 py-8">
                <h1 className="text-4xl font-bold text-dark-text mb-8">Deal Portal</h1>
                {categories.map(category => (
                  <CategorySection
                    key={category.id}
                    category={category}
                    products={products.filter(p => p.category === category.id)}
                    isOpen={openCategories.has(category.id)}
                    region={region}
                    onToggle={() => toggleCategory(category.id)}
                  />
                ))}
              </div>
            } />
            <Route path="/add" element={<AddProduct />} />
          </Routes>
        </main>

        <footer className="bg-dark-secondary p-4 mt-8">
          <div className="container mx-auto text-center text-dark-text-secondary">
            <p>© {new Date().getFullYear()} DealPortal. All rights reserved.</p>
            <p className="text-sm mt-2">
              As an Amazon Associate, we earn from qualifying purchases.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App; 